import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { logDebug, logError } from '@/utils/secureLogger';

/**
 * Check if a tutor has submitted Barangay or NBI clearance
 * This is required for tutors to conduct face-to-face sessions
 * 
 * @param tutorId - The Firebase UID of the tutor
 * @returns Promise<boolean> - True if clearance document exists, false otherwise
 */
export async function hasClearanceDocument(tutorId: string): Promise<boolean> {
  try {
    logDebug('Checking clearance document for tutor', { tutorId });
    
    const tutorRef = doc(db, 'users', tutorId);
    const tutorDoc = await getDoc(tutorRef);
    
    if (!tutorDoc.exists()) {
      logError('Tutor document not found', { tutorId });
      return false;
    }
    
    const tutorData = tutorDoc.data();
    
    // Check if the tutor has a clearance document uploaded
    const hasClearance = !!(tutorData.documents?.clearance?.url || tutorData.documents?.clearance);
    
    logDebug('Clearance check result', { tutorId, hasClearance });
    
    return hasClearance;
  } catch (error) {
    logError('Error checking tutor clearance', error);
    return false;
  }
}

/**
 * Get allowed session modes for a tutor based on their clearance status
 * 
 * @param tutorId - The Firebase UID of the tutor
 * @returns Promise<'online' | 'both'> - 'online' if no clearance, 'both' if clearance exists
 */
export async function getAllowedSessionModes(tutorId: string): Promise<'online' | 'both'> {
  const hasClearance = await hasClearanceDocument(tutorId);
  
  // If tutor has clearance, they can conduct both online and face-to-face sessions
  // If no clearance, they can only conduct online sessions
  return hasClearance ? 'both' : 'online';
}

/**
 * Check if a tutor can conduct face-to-face sessions
 * This requires both approval status and clearance document
 * 
 * @param tutorId - The Firebase UID of the tutor
 * @returns Promise<boolean> - True if tutor can conduct face-to-face sessions
 */
export async function canConductFaceToFaceSessions(tutorId: string): Promise<boolean> {
  try {
    const tutorRef = doc(db, 'users', tutorId);
    const tutorDoc = await getDoc(tutorRef);
    
    if (!tutorDoc.exists()) {
      return false;
    }
    
    const tutorData = tutorDoc.data();
    
    // Check if tutor is approved
    const isApproved = tutorData.profileStatus === 'approved' || 
                       tutorData.applicationStatus === 'approved';
    
    // Check if tutor has clearance document
    const hasClearance = await hasClearanceDocument(tutorId);
    
    logDebug('Face-to-face eligibility check', { 
      tutorId, 
      isApproved, 
      hasClearance,
      canConduct: isApproved && hasClearance 
    });
    
    return isApproved && hasClearance;
  } catch (error) {
    logError('Error checking face-to-face eligibility', error);
    return false;
  }
}

/**
 * Get a user-friendly message about clearance requirements
 * 
 * @param hasClearance - Whether the tutor has submitted clearance
 * @returns string - Message to display to the tutor
 */
export function getClearanceMessage(hasClearance: boolean): string {
  if (hasClearance) {
    return 'You have submitted your Barangay or NBI clearance. You can conduct both online and face-to-face sessions.';
  }

  return 'Without Barangay or NBI clearance, you can conduct online sessions only.';
}
